// Pushed andon notifications, and whether this device can receive them.
//
// Ported from the Andon app's notifications sheet. The list is what the
// server has pushed this person lately; the panel above it is the half of
// push the browser can see — see `lib/pushHealth` for the half it cannot —
// with a test button, because past every precondition the only honest check
// is to send one and look at the phone. Reached from More, with the andon
// preview on.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { andonApi } from '@/api/andon/client';
import type { PushTestVM } from '@/api/andon/types';
import { ApiError } from '@/api/http';
import { Icon } from '@/components/ui/Icon';
import { Sheet } from '@/components/ui/Sheet';
import { EmptyState, SectionLabel } from '@/components/ui/misc';
import { useT } from '@/i18n/useT';
import type { Translations } from '@/i18n/strings';
import { isStandalone } from '@/lib/installPrompt';
import { clearPushSnooze, pushCopy, pushHealth, pushNeedsAttention, subscribePushHealth, type PushHealth } from '@/lib/pushHealth';
import { enablePush } from '@/pwa';
import { useStore } from '@/store';

const NOTIFICATIONS_KEY = ['andon', 'notifications'] as const;

export function NotificationSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const openAndon = useStore((s) => s.openAndon);
  const operator = useStore((s) => s.operator);
  const { data } = useQuery({ queryKey: NOTIFICATIONS_KEY, queryFn: andonApi.notifications, enabled: open });

  const clear = useMutation({
    mutationFn: andonApi.clearNotifications,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });

  return (
    <Sheet open={open} onClose={onClose} title={t.notificationsT}>
      {/* Only with somebody confirmed on the device: without one there is
          nobody to address, and the server refuses — which is the device
          waiting for a sign-in rather than anything being wrong. */}
      {!!operator && <PushStatus devices={data?.devices ?? null} />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data?.items.map((n, i) => (
          <button
            key={`${n.tag}-${i}`}
            type="button"
            className="press-row"
            onClick={() => {
              onClose();
              if (n.alertId > 0) openAndon(n.alertId);
            }}
            style={{ padding: '13px 14px', borderRadius: 13, background: 'var(--surface2)', width: '100%', textAlign: 'left' }}
          >
            <span style={{ display: 'block', font: '700 12.5px Manrope,sans-serif', color: 'var(--ink)' }}>{n.title}</span>
            <span style={{ display: 'block', font: '500 11.5px/1.5 Manrope,sans-serif', color: 'var(--sub)', marginTop: 3 }}>{n.body}</span>
          </button>
        ))}
        {data?.items.length === 0 && <EmptyState>{t.nothingNewT}</EmptyState>}
      </div>
      {!!data?.items.length && (
        <button
          type="button"
          className="press"
          onClick={() => clear.mutate()}
          style={{
            marginTop: 12,
            width: '100%',
            height: 44,
            borderRadius: 13,
            background: 'var(--surface2)',
            color: 'var(--sub)',
            font: '600 13px Manrope,sans-serif',
          }}
        >
          {t.clear}
        </button>
      )}
    </Sheet>
  );
}

function PushStatus({ devices }: { devices: number | null }) {
  const t = useT();
  const [health, setHealth] = useState<PushHealth>(() => pushHealth());
  const [busy, setBusy] = useState(false);
  useEffect(() => subscribePushHealth(() => setHealth(pushHealth())), []);

  const test = useMutation({ mutationFn: andonApi.testPush });

  if (health === 'off') return null;

  const copy = pushCopy(health, isStandalone());
  const bad = pushNeedsAttention(health) || health === 'needs-install' || health === 'unsupported';

  const turnOn = () => {
    setBusy(true);
    // Clearing the snooze is the point of doing this from here rather than only
    // from the banner: somebody who came looking has un-dismissed the subject,
    // and if the fix does not take, the banner should be free to say so again.
    clearPushSnooze();
    void enablePush().finally(() => setBusy(false));
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <SectionLabel>{t.thisDeviceT}</SectionLabel>
      <div
        style={{
          padding: '13px 14px',
          borderRadius: 13,
          background: 'var(--surface2)',
          border: `1px solid ${bad ? 'var(--danger)' : 'var(--line)'}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <Icon
            name={bad ? 'warn' : 'bell'}
            size={16}
            strokeWidth={2.2}
            style={{ color: bad ? 'var(--danger)' : 'var(--pos)', flex: 'none', marginTop: 1 }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ font: '700 12.5px Manrope,sans-serif', color: 'var(--ink)' }}>{t[copy.title]}</div>
            <div style={{ font: '500 11.5px/1.5 Manrope,sans-serif', color: 'var(--sub)', marginTop: 3 }}>{t[copy.body]}</div>
            {/* Shown whenever the server has been asked, including — especially
                — when the answer is none. */}
            {devices != null && health !== 'needs-install' && health !== 'unsupported' && (
              <div style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)', marginTop: 5 }}>
                {devices === 0 ? t.noDeviceRegisteredT : `${devices} ${devices === 1 ? t.deviceT : t.devicesT} ${t.registeredToYouT}.`}
              </div>
            )}
          </div>
        </div>

        {copy.action && (
          <button
            type="button"
            className="press"
            onClick={turnOn}
            disabled={busy}
            style={{
              marginTop: 11,
              width: '100%',
              height: 42,
              borderRadius: 12,
              background: 'var(--accent)',
              color: 'var(--accentInk)',
              font: "700 13px 'Space Grotesk',sans-serif",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? t.askingT : t[copy.action]}
          </button>
        )}

        {health === 'ok' && (
          <>
            <button
              type="button"
              className="press"
              onClick={() => test.mutate()}
              disabled={test.isPending}
              style={{
                marginTop: 11,
                width: '100%',
                height: 42,
                borderRadius: 12,
                background: 'var(--surface)',
                border: '1px solid var(--line)',
                color: 'var(--ink)',
                font: "600 13px 'Space Grotesk',sans-serif",
                opacity: test.isPending ? 0.6 : 1,
              }}
            >
              {test.isPending ? t.sendingT : t.sendTestPushT}
            </button>
            {(test.data || test.error) && (
              <div style={{ font: '500 11.5px/1.5 Manrope,sans-serif', color: 'var(--sub)', marginTop: 8, textAlign: 'center' }}>
                {testResult(test.data ?? null, test.error, t)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function testResult(data: PushTestVM | null, error: unknown, t: Translations): string {
  if (error) return error instanceof ApiError ? error.message : t.pushTestFailedT;
  if (!data) return '';
  if (data.throttled) return t.pushTestThrottledT;
  if (data.devices === 0) return t.pushTestNoDeviceT;
  if (data.sent === 0) return t.pushTestRejectedT;
  // Sent is not delivered, and this is the sentence that says so. What is left
  // between here and a buzz is entirely outside the browser's sight.
  return `${t.sentToT} ${data.sent} ${data.sent === 1 ? t.deviceT : t.devicesT}. ${t.pushTestSentHintT}`;
}
